const __RELATIVE_SOURCE_FILE__ /* __INJECTED__ */ = 'packages/patapp_web/src/config/zgks/eCheckin/sections/Versicherung.tsx';
import * as React from 'react';
import { forwardRef } from 'react';
import { useIntl } from 'react-intl';
import { useCIS4m, useCIS4mData } from '@cistec/common_web/modules/CIS4m/hooks';
import { ECheckinSectionProps } from 'App/components/eCheckin/types';
import ECheckinSection from 'App/components/eCheckin/components/ECheckinSection';
import { createFieldPath } from 'App/components/eCheckin/utils';
import { VersicherungField } from 'App/components/eCheckin/components/fields/metakat/VersicherungField';
import textMap from '../translations';
import { CIS4mTextField } from '@cistec/common_web/modules/CIS4m/components/CIS4mTextField';
import _ from 'lodash';
import { CIS4mBlock } from '@cistec/common_web/modules/CIS4m/components/CIS4mBlock';
import { YesNoField } from 'App/components/eCheckin/components/fields/YesNoField';
import BehandlungsgrundField from 'App/components/eCheckin/components/fields/metakat/BehandlungsgrundField';
import { AddressBlock, AddressFieldsConfig } from 'App/components/eCheckin/components/fields/AddressBlock';
import { VersicherungsdeckungField } from 'App/components/eCheckin/components/fields/metakat/VersicherungsdeckungField';
import { useText } from '@cistec/common_web/utils/intlUtils';
import { Divider } from '@mui/material';
import { LabeledDivider } from 'App/components/eCheckin/components/LabeledDivider';
import { CIS4mDatePicker } from '@cistec/common_web/modules/CIS4m/components/CIS4mDatePicker';
import moment from 'moment';
import CIS4mSingleCheckbox from '@cistec/common_web/modules/CIS4m/components/CIS4mSingleCheckbox';

const addressFieldsConfig: AddressFieldsConfig = {
    streetAndNumber: { included: false },
    additionalInfo: { included: false },
    country: { included: false },
};

export const Versicherung = forwardRef((props: ECheckinSectionProps, ref: any) => {
    const { path } = props;
    const title = useText(props.title);

    const PATH_BEHANDLUNGSGRUND = createFieldPath(path, 'behandlungsgrund');
    const PATH_VERSICHERUNG = createFieldPath(path, 'versicherungSel');

    const PATH_ARBEITGEBER = createFieldPath(path, 'arbeitgeber');
    const PATH_UNFALL_DATE = createFieldPath(path, 'unfallDatum');

    const PATH_VERSICHERUNG_NAME = createFieldPath(path, 'versicherungName');
    const PATH_VERSICHERUNG_DECKUNG = createFieldPath(path, 'klasse');
    const PATH_VERSICHERTENNR = createFieldPath(path, 'versichertenNr');
    const PATH_VERSICHERUNG_CARDNR = createFieldPath(path, 'kartenNr');
    const PATH_VERSICHERUNG_CLAIMNR = createFieldPath(path, 'schadenNr');
    const PATH_VERSICHERUNG_CLAIMNR_UNKNOWN = createFieldPath(path, 'schadenNrNichtBekannt');

    const PATH_ZUSATZVERSICHERUNG = createFieldPath(path, 'zusatzVersicherung');
    const PATH_ZUSATZVERSICHERUNG_NAME = createFieldPath(path, 'zusatzVersicherungName');

    const { formatMessage } = useIntl();

    const formData = useCIS4mData();

    const versicherung = _.get(formData, PATH_VERSICHERUNG, '');
    const hideArbeitgeberBlock = !['unfallversicherung'].includes(versicherung);
    const hasZusatzversicherung = _.get(formData, PATH_ZUSATZVERSICHERUNG) === 'ja';
    const hideUnfallversicherung = _.get(formData, PATH_VERSICHERUNG) !== 'unfallversicherung';

    const isSchadenNrRequired = _.get(formData, PATH_VERSICHERUNG_CLAIMNR_UNKNOWN) !== '1';

    const eCheckinSectionRef = useCIS4m(ref);
    return (
        <ECheckinSection ref={eCheckinSectionRef} path={path} title={title}>
            <h1>{title}</h1>
            <BehandlungsgrundField label={textMap.behandlungsgrund} path={PATH_BEHANDLUNGSGRUND} required={true} />
            <VersicherungField label={textMap.zustVersicherung} path={PATH_VERSICHERUNG} required={true} />
            <CIS4mBlock hidden={hideArbeitgeberBlock}>
                <LabeledDivider text={textMap.arbeitgeber} />
                <CIS4mTextField label={textMap.arbeitgeber} path={PATH_ARBEITGEBER} required={true} />
                <AddressBlock basePath={path} required={true} fieldsConfig={addressFieldsConfig} />
                <Divider />
            </CIS4mBlock>

            <CIS4mTextField label={textMap.versicherungName} path={PATH_VERSICHERUNG_NAME} required={true} />
            <VersicherungsdeckungField label={textMap.klasse} path={PATH_VERSICHERUNG_DECKUNG} required={true} />
            <CIS4mTextField
                label={textMap.versichertenNr}
                path={PATH_VERSICHERTENNR}
                required={true}
                helperText={formatMessage(textMap.versichertenNrHelpText)}
                hidden={!hideUnfallversicherung}
            />
            <CIS4mDatePicker
                label={formatMessage(textMap.unfallDatum)}
                path={PATH_UNFALL_DATE}
                hidden={hideUnfallversicherung}
                maxDate={moment()}
                required={true}
            />
            <CIS4mTextField
                label={textMap.kartenNr}
                path={PATH_VERSICHERUNG_CARDNR}
                required={true}
                hidden={!hideUnfallversicherung}
            />
            <CIS4mBlock hidden={hideUnfallversicherung}>
                <CIS4mTextField
                    label={textMap.schadenNr}
                    path={PATH_VERSICHERUNG_CLAIMNR}
                    required={isSchadenNrRequired}
                />
                <CIS4mSingleCheckbox label={textMap.schadenNrNichtBekannt} path={PATH_VERSICHERUNG_CLAIMNR_UNKNOWN} />
            </CIS4mBlock>

            <YesNoField
                label={textMap.zusatzVersicherung}
                path={PATH_ZUSATZVERSICHERUNG}
                required={true}
                yesValue="ja"
            />
            <CIS4mTextField
                label={textMap.zusatzVersicherungName}
                path={PATH_ZUSATZVERSICHERUNG_NAME}
                required={true}
                hidden={!hasZusatzversicherung}
            />
        </ECheckinSection>
    );
});

Versicherung.displayName = 'Versicherung';

export default Versicherung;
